import { css, useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import IconOpen from "@mui/icons-material/ArrowForwardTwoTone";
import { useContext, useMemo } from "react";
import { Link } from "react-router";
import type { SearchHit } from "../../../../api/src/algolia";
import { PublicationContext } from "../contexts/WithPublication/PublicationContext";
import { UserPublicationsContext } from "../contexts/WithPublications/UserPublicationsContext";
import { renderHighlightedText } from "./annotations/utils";
import { viewInPublicationCSS } from "./cssHelpers";
import { FlexColContainer, FlexRowContainer } from "./layout";
import { frjArticleUrlToCompassUrl } from "./frjUtils";

const SearchResultContent = styled.div`
  border-radius: 2px;
  border: 1px solid #fff;
  padding: 4px;
  word-break: break-word;

  box-shadow:
    0px 0px 4px 2px rgba(224, 222, 214, 1) inset,
    0px 0px 0px 0.5px rgba(252, 251, 248, 1) inset,
    0px -6px 12px -2px rgba(255, 255, 255, 1) inset,
    0px 0px 12px -6px rgba(211, 209, 199, 1);
  opacity: 1;
  background-color: ${({ theme }) => theme.colours.accent.light._20};

  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  p,
  li,
  table,
  blockquote {
    font-size: 12px;
  }

  /* fix for orphaned list items in search results */
  li:not(ul *):not(ol *) {
    margin-left: 30px;
  }

  ${({ theme }) => theme.customTypography.book.label._3};
  font-weight: normal;
  .search-highlight {
    background-color: rgba(181, 184, 196, 0.3);
    font-style: normal;
    font-weight: 700;
  }
`;

const SearchResultContentContainer = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return (
    <div
      css={({ colours }) => css`
        padding: 2px;
        border-radius: 6px 6px 0px 6px;
        opacity: 1;
        background-color: ${colours.uIGrey.light._40};
        width: 100%;
      `}
    >
      <div
        css={({ layers }) => css`
          border-radius: 6px;
          padding: 4px;
          ${layers.relief.uiGrey_10.shadowHigher}
        `}
      >
        {children}
      </div>
    </div>
  );
};

type FRJSearchRecord = {
  headings?: string[];
} & SearchHit;

const SearchResultInfo = ({
  searchResult,
  goToSection,
}: {
  searchResult: SearchHit | FRJSearchRecord;
  goToSection?: (sectionHash: string) => void;
}) => {
  const { publicationsBySlug } = useContext(UserPublicationsContext);
  const theme = useTheme();
  const { setScrollSnippet } = useContext(PublicationContext);

  const hashLink = searchResult.objectID.includes("_part")
    ? searchResult.objectID.split("_part")[0]
    : searchResult.objectID;
  return (
    <FlexRowContainer
      css={css`
        justify-content: space-between;
        width: 100%;
        ${theme.layers.relief.uiGrey_10.shadow}
        border-radius: 0px 0px 6px 6px;
      `}
      style={{
        ...theme.customTypography.label._4,
        color: theme.colours.accent.dark._20,
      }}
    >
      <FlexRowContainer
        style={{
          padding: "0px 0px 0px 8px",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          width: "1%",
          flexGrow: "1",
          justifyContent: "flex-start",
          ...theme.customTypography.label._4,
          color: theme.colours.accent.dark._20,
        }}
      >
        {goToSection ? null : (
          <>
            <div
              style={{
                fontWeight: 800,
              }}
            >
              {searchResult.frjRecord
                ? "FRJ"
                : publicationsBySlug?.[searchResult.publicationId]?.title}
            </div>
            <div>●</div>
          </>
        )}
        <div>{searchResult.chapterTitle}</div>
        {searchResult.frjRecord ? (
          !!("headings" in searchResult && searchResult.headings?.length) && (
            <>
              <div>●</div>
              <div>{searchResult.headings[0]}</div>
            </>
          )
        ) : searchResult.h2Title ? (
          <>
            <div>●</div>
            <div title={searchResult.h2Title}>{searchResult.h2Title}</div>
          </>
        ) : null}
      </FlexRowContainer>
      <div
        css={({ colours }) => css`
          border-radius: 0px 0px 6px 6px;
          padding: 0px 2px 2px 2px;
          opacity: 1;
          background-color: ${colours.uIGrey.light._40};
        `}
      >
        {searchResult.frjRecord && searchResult.url ? (
          <Link
            to={frjArticleUrlToCompassUrl(searchResult.url)}
            css={viewInPublicationCSS}
          >
            View at source
            <IconOpen sx={{ height: "14px", width: "14px" }} />
          </Link>
        ) : goToSection ? (
          <button
            onClick={() => {
              const snippet = searchResult._snippetResult.content?.value;
              if (snippet) setScrollSnippet(snippet);
              goToSection(hashLink);
            }}
            css={viewInPublicationCSS}
          >
            View
            <IconOpen sx={{ height: "14px", width: "14px" }} />
          </button>
        ) : (
          <Link
            to={{
              pathname: `/pub/${searchResult.publicationId}`,
              hash: hashLink,
            }}
            css={viewInPublicationCSS}
          >
            View at source
            <IconOpen sx={{ height: "14px", width: "14px" }} />
          </Link>
        )}
      </div>
    </FlexRowContainer>
  );
};

export const SearchResult = ({
  searchResult,
  goToSection,
  searchTerm,
  cropTo,
}: {
  searchResult: SearchHit;
  goToSection?: (sectionHash: string) => void;
  searchTerm?: string;
  cropTo?: number;
}) => {
  const { publicationsBySlug } = useContext(UserPublicationsContext);
  const content = useMemo(() => {
    if (cropTo && cropTo > 200) {
      const { _highlightResult, previousHtml, nextHtml } = searchResult;
      return (
        (previousHtml || "") +
        _highlightResult.content?.value +
        (nextHtml || "")
      );
    }
    if (cropTo) {
      return searchResult._highlightResult.content?.value;
    }
    return searchResult._snippetResult.content?.value;
  }, [searchResult, cropTo]);
  // Crop the content to a maximum of 200 characters or more if the
  // first instance of the matched after that
  const matchedItemPosition = useMemo(
    () => content?.match(/<em class="search-highlight">(.*?)<\/em>/),
    [content],
  );

  const croppedContent = useMemo(() => {
    const matchedItemPositionIndex =
      (matchedItemPosition?.[0].length ?? 0) +
      (matchedItemPosition?.index ?? 0);
    if (!content) return null;
    if (!cropTo) return content;
    if (content?.length < cropTo) return content;
    if (
      cropTo > 200 &&
      ["table", "blockquote", "ol", "ul"].includes(searchResult.type)
    )
      return content;

    const div = document.createElement("div");
    div.innerHTML = content?.substring(
      0,
      matchedItemPositionIndex > cropTo ? matchedItemPositionIndex : cropTo,
    );

    return div.innerHTML + "...";
  }, [matchedItemPosition, content, cropTo, searchResult.type]);

  if (!croppedContent) {
    return null;
  }

  return (
    <div
      css={({ layers }) => css`
        ${layers.relief.uiGrey_10.shadow}
        margin-bottom: 0.5rem;
        border-radius: 6px;
      `}
    >
      <FlexColContainer
        css={css`
          align-items: flex-end;
        `}
      >
        <SearchResultContentContainer>
          <SearchResultContent>
            {renderHighlightedText(
              matchedItemPosition ? croppedContent : searchResult.content,
              publicationsBySlug?.[searchResult.publicationId]?.filename ?? "",
              searchResult.publicationId,
              matchedItemPosition ? undefined : searchTerm,
            )}
          </SearchResultContent>
        </SearchResultContentContainer>
        <SearchResultInfo
          searchResult={searchResult}
          goToSection={goToSection}
        />
      </FlexColContainer>
    </div>
  );
};
